import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { AppLocale } from "../i18n/format";

/**
 * A modal bottom sheet.
 *
 * PORTALLED TO <body> ON PURPOSE. `position: fixed` is measured against the
 * nearest ancestor that has a transform, filter or backdrop-filter rather than
 * the viewport. A currency row already carries a transform while dragging, and
 * any future form is free to add one to a wrapper — at which point a sheet
 * rendered inline would silently anchor to the wrong box. The portal removes
 * that whole class of breakage instead of relying on nobody ever adding a
 * transform above it.
 *
 * WHY IT DOES NOT MIRROR. The sheet enters from the bottom edge and leaves the
 * same way. That is borrowed from a physical drawer, not from the act of
 * reading a line, so under the project's mirroring test it stays put in RTL.
 * Only its text and the reading order of its rows localise. Compare the keypad
 * (does not mirror) with swipe-to-delete (does).
 */

/** How far the sheet must travel before the drag counts as a dismissal. */
const DISMISS_PX = 96;

/**
 * Downward flick speed that dismisses regardless of distance, in px/ms.
 *
 * Distance alone punishes a fast, short flick — the most natural way to throw a
 * sheet away — by snapping it back. Velocity alone dismisses a slow, careful
 * drag the user was still deciding about. Either condition passing is what
 * matches how the gesture actually feels.
 */
const DISMISS_VELOCITY = 0.5;

/** Elements that can hold focus, in DOM order. Used by the trap. */
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly locale: AppLocale;
  /** Names the dialog for assistive tech; rendered as the sheet's heading. */
  readonly title: string;
  /** Optional line under the title — never the only place information lives. */
  readonly subtitle?: string;
  readonly children: React.ReactNode;
}

export function Sheet({ open, onClose, locale, title, subtitle, children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useRef(`sheet-title-${Math.random().toString(36).slice(2, 9)}`);

  /** Live drag offset in px, always >= 0 — the sheet never travels upward. */
  const [dragY, setDragY] = useState(0);
  const [settling, setSettling] = useState(false);
  const drag = useRef<{ id: number; startY: number; startedAt: number } | null>(null);

  /*
   * Focus goes into the sheet on open and comes back to the trigger on close.
   *
   * Restoring is the half people forget: without it, closing a sheet drops
   * focus onto <body> and the next Tab restarts from the top of the page, which
   * on this screen means walking the whole currency list again to get back to
   * the row you were on.
   */
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // The panel itself, not the first control: a screen reader should hear the
    // sheet's name before its options, and nothing is pre-selected for a
    // keyboard user who may want to Escape straight back out.
    panel?.focus();
    return () => {
      const target = returnFocusTo.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [open]);

  /*
   * Scroll lock. The page keeps its scrollbar gutter (html has
   * scrollbar-gutter: stable) so locking does not shift the layout sideways.
   */
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = previous;
    };
  }, [open]);

  /* Escape closes from anywhere inside, and Tab is trapped to the panel. */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      /*
       * No visibility filter here, deliberately. The first version screened on
       * `offsetParent !== null`, which is null for everything in a layout-less
       * environment and is also null for any `position: fixed` element in a
       * real browser — so the trap silently found zero controls and parked
       * focus on the panel forever. The sheet's contents are ours by
       * construction; aria-hidden is the only exclusion worth honouring.
       */
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.getAttribute("aria-hidden") !== "true",
      );
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onClose]);

  /* Reset the drag whenever the sheet opens, so a dismissed sheet does not
     reopen already pushed halfway down. */
  useEffect(() => {
    if (open) {
      setDragY(0);
      setSettling(false);
    }
  }, [open]);

  if (!open) return null;

  const endDrag = (clientY: number) => {
    const info = drag.current;
    drag.current = null;
    if (!info) return;
    const travelled = Math.max(0, clientY - info.startY);
    const elapsed = Math.max(1, performance.now() - info.startedAt);
    const velocity = travelled / elapsed;
    if (travelled > DISMISS_PX || velocity > DISMISS_VELOCITY) {
      onClose();
      return;
    }
    // Snap back. The transition is re-enabled only for the return trip, so the
    // sheet tracks the finger exactly on the way down and eases on the way back.
    setSettling(true);
    setDragY(0);
  };

  const grabProps = {
    onPointerDown: (event: React.PointerEvent) => {
      if (!event.isPrimary) return;
      drag.current = {
        id: event.pointerId,
        startY: event.clientY,
        startedAt: performance.now(),
      };
      setSettling(false);
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture is unavailable in jsdom and on some older engines. The move
        // and up handlers still fire on the element, so the gesture degrades to
        // "works unless the finger leaves the handle" rather than throwing.
      }
    },
    onPointerMove: (event: React.PointerEvent) => {
      const info = drag.current;
      if (!info || info.id !== event.pointerId) return;
      // Downward only: dragging up must not lift the sheet off the bottom edge
      // and expose the page beneath it.
      setDragY(Math.max(0, event.clientY - info.startY));
    },
    onPointerUp: (event: React.PointerEvent) => endDrag(event.clientY),
    onPointerCancel: (event: React.PointerEvent) => endDrag(event.clientY),
  };

  const ar = locale === "ar";

  return createPortal(
    <div className="sheet-root" data-open="true">
      {/*
       * The scrim is a plain div, not a <button>: it is announced by nothing,
       * and Escape plus the explicit close button are the accessible paths out.
       * A focusable full-screen element would be a tab stop with no name.
       */}
      <div className="sheet-scrim" onClick={onClose} data-testid="sheet-scrim" />

      <div
        ref={panelRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId.current}
        tabIndex={-1}
        data-settling={settling ? "true" : undefined}
        style={{ "--sheet-drag-y": `${dragY}px` } as React.CSSProperties}
        onTransitionEnd={() => setSettling(false)}
      >
        {/*
         * The grab handle carries the drag, not the whole panel. A sheet whose
         * entire surface is draggable cannot contain a scrolling list without
         * the two gestures fighting, and this sheet will hold the currency
         * picker next.
         */}
        <div className="sheet__grab" {...grabProps}>
          <span className="sheet__grab-bar" aria-hidden="true" />
        </div>

        <header className="sheet__head">
          <div className="sheet__titles">
            <h2 className="sheet__title" id={headingId.current}>
              {title}
            </h2>
            {subtitle ? <p className="sheet__subtitle">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="sheet__close"
            onClick={onClose}
            aria-label={ar ? "إغلاق" : "Close"}
          >
            <span className="sheet__close-glyph" aria-hidden="true">
              ×
            </span>
          </button>
        </header>

        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
